import React, { useEffect, useMemo, useRef, useState, useContext } from "react";

import { Column, usePagination, useSortBy, useTable } from 'react-table';

import TableCSVExport from '../../../component/TableCSVExport';

// Helper.
import Helper from '../../../helpers/Helper';

// Types.
import Alert from '../../Alert';
import ProgramsDataTable from '../../../types/instance/tables/ProgramsDataTable';
import { FilterType } from '../../../typescript/Enums';

// Components.
import GenericBreakdown from '../../GenericBreakdown';
import ReactTable from '../../ReactTable';

// Constants.
import { getDefaultPageSize } from "../../../helpers/utils";
import { useLocation, useNavigate } from "react-router-dom";

// Contexts.
import { FilterContext } from '../../../context/FilterContext';
import SearchInput from '../../SearchInput';

function ProgramsTable(props: { instanceId: number, batchId?: string, statementId?: string, instanceTime: number, programsDataTable: ProgramsDataTable[], setFilterOptions: Function, currentTab?: string, tabIdPrefix?: string }) {
    const tabIdPrefix = props.tabIdPrefix || '';
    const inputRef = useRef<HTMLInputElement>(null);
    const navigate = useNavigate();
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const [tableFilter, setTableFilter] = useState(tabIdPrefix ? "" : (query.get("filter") || ""));
    const initialPageIndex = Number(query.get("page") || 0);
    const initialPageSize = Number(query.get("pageSize") || getDefaultPageSize());

    const {instanceTime, setFilterOptions} = props;
    
    const { currentFilterOptions } = useContext(FilterContext);

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus({ preventScroll: true });
        }
    }, []);

    function clearTableFilter() {

        // Clear any pre-existing filter values.
        setTableFilter('');
    }

    const data = useMemo(() => props.programsDataTable.filter(row => (row.program.toLowerCase().includes(tableFilter.toLowerCase()))), [props.programsDataTable, tableFilter]);

    const columns: Column[] = useMemo(() => {
        function filterData(filter: string): void {
            setFilterOptions(Helper.updateFilterOptions
            (FilterType.Programs, filter, currentFilterOptions));
        }

        return [
            {
                accessor: 'color',
                disableSortBy: true
            },
            {
                accessor: 'id',
                disableSortBy: true
            },
            {
                Header: 'Program',
                accessor: 'program',
                className: 'fw-bold',
                defaultCanSort: true,
                sortType: 'alphanumeric',
                Cell: ({cell}) => (
                    <React.Fragment>
                        <span className="me-1 ps-1" style={{backgroundColor: cell.row.values.color}}>&nbsp;</span>
                        {(cell.row.values.program !== '-') ? (
                            <button key={cell.column.id} className="btn btn-sm btn-link btn-filter"
                                    onClick={() => filterData(cell.row.values.program)}>{cell.row.values.program}</button>
                        ) : (
                            <React.Fragment>-</React.Fragment>
                        )}
                    </React.Fragment>
                ),
            },
            {
                Header: 'Total Time',
                accessor: 'breakdown',
                className: 'generic-waits text-nowrap',
                disableSortBy: true,
                Cell: ({cell}) => (
                    cell.row.values.waittime !== null ? (
                        cell.row.values.id < 20 ? (
                            <GenericBreakdown individualTime={cell.row.values.waittime} totalTime={instanceTime}
                                              color={((cell.row.values.color !== '') ? cell.row.values.color : '#999')}
                                              tooltip={cell.row.values.program}/>
                        ) : (
                            <React.Fragment>-</React.Fragment>
                        )
                    ) : (
                        <React.Fragment>-</React.Fragment>
                    )
                )
            },
            {
                Header: 'Wait Time',
                accessor: 'waittime',
                headerClassName: 'text-end',
                className: 'text-end',
                defaultCanSort: true,
                sortType: (rowA, rowB, columnId) => {
                    const aWait = Number(rowA.values[columnId]) || 0;
                    const bWait = Number(rowB.values[columnId]) || 0;

                    if (aWait !== bWait) {
                        return bWait - aWait;
                    }

                    const aProgram = rowA.values['program'] || '';
                    const bProgram = rowB.values['program'] || '';
                    return aProgram.localeCompare(bProgram);
                },
                Cell: ({cell}) => (
                    cell.row.values.waittime !== null ? (
                        Helper.getTimeInEnglish(cell.row.values.waittime)
                    ) : (
                        <React.Fragment>-</React.Fragment>
                    )
                )
            },
            {
                Header: 'Weight %',
                accessor: 'weight',
                headerClassName: 'text-end',
                className: 'text-end',
                defaultCanSort: true,
                sortType: 'basic',
                Cell: ({ cell }) => {
                    const { waittime } = cell.row.values;
                    const percentage = (100 / props.instanceTime) * waittime;

                    return (
                            <React.Fragment>
                                {props.instanceTime ? `${Helper.formatPercentage(percentage)}%` : '-'}
                            </React.Fragment>
                    );
                }
            }
        ]
    }, [instanceTime, currentFilterOptions, setFilterOptions]);

    const tableInstance = useTable({
            columns,
            data,
            initialState: {
                hiddenColumns: ['id', 'color'],
                pageIndex: initialPageIndex,
                pageSize: initialPageSize,
            }
        },
        useSortBy, usePagination);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: {pageIndex, pageSize}
    } = tableInstance;

    useEffect(() => {
        // Don't update URL params when in panel mode (tabIdPrefix is set)
        if (tabIdPrefix) return;
        
        const params = new URLSearchParams(location.search);

        if (tableFilter) {
            params.set('filter', tableFilter);
        } else {
            params.delete('filter');
        }

        params.set('page', pageIndex.toString());
        params.set('pageSize', pageSize.toString());

        navigate({ search: params.toString() }, { replace: true });
    }, [tableFilter, pageIndex, pageSize, tabIdPrefix]);

    useEffect(() => {
        if (tableFilter) {
            gotoPage(0);
        }
    }, [tableFilter]);

    useEffect(() => {
        // Clear Filter if tab is changed
        if (props.currentTab) {
            setTableFilter('')
        }
    }, [props.currentTab]);

    return (
        <div id={`${tabIdPrefix}programs`} className="tab-pane fade show" role="tabpanel" aria-labelledby={`${tabIdPrefix}programs-tab`}>
            <div className="card">
                <div className="card-header">
                    <i className="fal fa-computer-classic fa-fw" aria-hidden="true"/>
                    Programs
                    <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                       data-bs-target="#collapsePrograms" aria-expanded="false" aria-controls="collapsePrograms"/>
                </div>
                <div id="collapsePrograms" className="card-body collapse show">
                    {Number(process.env.REACT_APP_API_LIMIT) - 1 === data.length - 1 && (
                        <Alert
                            message={`To help preserve performance, we limit the total number of program records below to the top ${Number(process.env.REACT_APP_API_LIMIT) - 1} in descending order by their total wait time.`}
                            heading="Program records" variant="alert-info"/>
                    )}
                    <div className="row row-cols-1 row-cols-md-2 table-search">
                        <div className="col col-md-10">
                            <TableCSVExport
                                tableInstance={tableInstance}
                                filename="Programs"
                                excludeColumns={['breakdown']}
                                instanceTime={instanceTime}
                            />
                        </div>
                        <div className="col col-md-2">
                            <SearchInput
                                ref={inputRef}
                                value={tableFilter}
                                onChange={(e) => setTableFilter(e.target.value)}
                                onClear={clearTableFilter}
                            />
                        </div>
                    </div>
                    <ReactTable
                        getTableProps={getTableProps}
                        getTableBodyProps={getTableBodyProps}
                        headerGroups={headerGroups}
                        prepareRow={prepareRow}
                        page={page}
                        canPreviousPage={canPreviousPage}
                        canNextPage={canNextPage}
                        pageOptions={pageOptions}
                        pageCount={pageCount}
                        gotoPage={gotoPage}
                        nextPage={nextPage}
                        previousPage={previousPage}
                        setPageSize={setPageSize}
                        pageIndex={pageIndex}
                        pageSize={pageSize}
                        dataLength={data.length}
                        error={null}
                        loading={false}
                    />
                </div>
            </div>
        </div>
    );
}

export default ProgramsTable;
